/**
 * Error state component
 */

import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

interface ErrorStateProps {
  type: 'no_key' | 'not_found' | 'revoked' | 'decrypt_failed' | 'network' | 'unknown';
}

// Heroicons outline SVG components (24x24 viewBox, stroke-based)
const LockClosedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>
);

const WifiOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 0 1 1.06 0Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
  </svg>
);

export default function ErrorState({ type }: ErrorStateProps) {
  const { t } = useTranslation('common');
  const localizedPath = useLocalizedPath();

  const getErrorContent = () => {
    switch (type) {
      case 'network':
        return {
          title: t('errorState.connectionError'),
          description: t('errorState.connectionDescription'),
          icon: <WifiOffIcon />,
          bgColor: 'bg-blue-500',
        };
      default:
        return {
          title: t('errorState.contentUnavailable'),
          description: t('errorState.contentDescription'),
          icon: <LockClosedIcon />,
          bgColor: 'bg-gray-500',
        };
    }
  };

  const { title, description, icon, bgColor } = getErrorContent();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Animated background gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-waffle-orange/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-amber-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative text-center max-w-md w-full">
        <div className="rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-2xl">
          <div className={`w-16 h-16 rounded-full ${bgColor} flex items-center justify-center text-white mx-auto mb-4`}>
            {icon}
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">{title}</h1>
          <p className="text-sm sm:text-base text-gray-300 mb-6">{description}</p>
          <a
            href={localizedPath('/')}
            className="inline-block px-6 py-3 bg-gradient-to-r from-waffle-orange to-amber-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-waffle-orange/25 transition-all min-h-[44px] text-sm sm:text-base mb-6"
          >
            {t('errorState.goToHomepage')}
          </a>

          {/* Download buttons on error state */}
          <div className="pt-4 border-t border-white/10">
            <p className="text-xs sm:text-sm text-gray-400 mb-3">{t('errorState.downloadPrompt')}</p>
            <div className="flex flex-row gap-2 justify-center">
              <a
                href="https://apps.apple.com/app/family-waffle"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-all min-h-[44px]"
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[8px] leading-tight opacity-90">{t('errorState.downloadOnThe')}</div>
                  <div className="text-xs font-semibold leading-tight">{t('errorState.appStore')}</div>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.familywaffle"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-all min-h-[44px]"
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[8px] leading-tight opacity-90">{t('errorState.getItOn')}</div>
                  <div className="text-xs font-semibold leading-tight">{t('errorState.googlePlay')}</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
